import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logBreadcrumb } from '../../services/errorHandler'
import { isMobileDevice } from '../../utils/device'
import { ActionCard } from './design/ActionCard'
import { AppDownloadCard } from './design/AppDownloadCard'
import { AuthLayout } from './design/AuthLayout'
import { LockIcon } from './design/icons'
import { COLORS, FONT } from './design/tokens'

// Safety reset for the per-card pending state. The "Open in app" action is now
// an https Universal/App Link: when the app is installed it opens and this SPA
// stays backgrounded (this timer then transitions the one-time card to a clear
// "Already used" state); when the app is absent the browser navigates away to
// coachee-web (which round-trips to the store) and this never fires. For a
// reusable link the card simply becomes tappable again.
const PENDING_RESET_TIMEOUT_MS = 8_000

type CardState = 'idle' | 'pending' | 'consumed'

interface Props {
    /**
     * Pre-built https Universal (iOS) / App (Android) Link for "Open in Sama
     * app", on coachee.sama.io. Built by LoginCoacheePage via `buildAppLinkUrl`
     * so the chooser stays a pure presentation component. Rendered as an
     * `<a href>` (not a JS redirect) because Universal/App Links only fire from
     * a genuine anchor navigation. The origin is env-sourced and path segments
     * are `encodeURIComponent`-encoded; no user-controlled input reaches it.
     */
    openInAppUrl: string
    /** Fires when the user picks "Continue in browser". */
    onContinueOnWeb: () => void
    appStoreUrl: string
    playStoreUrl: string
    /**
     * True when the chooser was reached with a `magicToken` query param. Both
     * actions then forward to a one-time-use endpoint, so after activation the
     * card transitions to a permanent "consumed" state instead of resetting.
     */
    isOneTimeMagicTokenLink: boolean
}

export const MagicLinkChooser: React.FC<Props> = ({
    openInAppUrl,
    onContinueOnWeb,
    appStoreUrl,
    playStoreUrl,
    isOneTimeMagicTokenLink,
}) => {
    const { t } = useTranslation()
    const [openInAppState, setOpenInAppState] = useState<CardState>('idle')
    const [continueOnWebState, setContinueOnWebState] =
        useState<CardState>('idle')
    const [statusMessage, setStatusMessage] = useState('')
    const openInAppResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    )
    const continueOnWebResetTimerRef = useRef<
        ReturnType<typeof setTimeout> | null
    >(null)

    // Mobile frames a choice ("How to continue?" + 2 actions); desktop has only
    // the browser path, so it uses the sign-in heading + a single action. Cards
    // are intentionally minimal — one line, no icon or description — and the
    // filled primary card carries the "recommended" weight on its own (top +
    // viridian fill), so no separate badge is needed.
    const mobile = isMobileDevice()
    const pageTitle = mobile
        ? t('MagicLinkChooser.title')
        : t('titles.logIn')
    const continueCardTitle = mobile
        ? t('MagicLinkChooser.continueOnWeb')
        : t('MagicLinkChooser.continueToSama')
    // On desktop the continue card is the only action, so it carries the primary
    // visual weight (viridian fill) instead of the outlined secondary look used
    // on mobile (where it sits below the recommended app card).
    const continueCardIsPrimary = !mobile

    const cleanupResetTimers = useCallback(() => {
        if (openInAppResetTimerRef.current) {
            clearTimeout(openInAppResetTimerRef.current)
            openInAppResetTimerRef.current = null
        }
        if (continueOnWebResetTimerRef.current) {
            clearTimeout(continueOnWebResetTimerRef.current)
            continueOnWebResetTimerRef.current = null
        }
    }, [])

    // One-time magic-token landings: both actions forward to the same
    // single-use endpoint, so tapping either must lock the *whole* chooser,
    // not just the tapped card. Otherwise a user could tap "Open in app",
    // come back via back / bfcache, and burn the second redirect on a token
    // that's already been consumed.
    const setAllCardStates = useCallback<
        React.Dispatch<React.SetStateAction<CardState>>
    >((next) => {
        setOpenInAppState(next)
        setContinueOnWebState(next)
    }, [])

    useEffect(() => {
        logBreadcrumb('magic_link_chooser_shown')
        return () => {
            cleanupResetTimers()
        }
    }, [cleanupResetTimers])

    const scheduleReset = useCallback(
        (
            timerRef: React.MutableRefObject<
                ReturnType<typeof setTimeout> | null
            >,
            setState: React.Dispatch<React.SetStateAction<CardState>>,
        ) => {
            timerRef.current = setTimeout(() => {
                timerRef.current = null
                if (isOneTimeMagicTokenLink) {
                    logBreadcrumb('magic_link_consumed_shown')
                    setStatusMessage(
                        t('MagicLinkChooser.linkAlreadyUsedStatus'),
                    )
                    setState('consumed')
                } else {
                    logBreadcrumb('magic_link_pending_reset')
                    setState('idle')
                }
            }, PENDING_RESET_TIMEOUT_MS)
        },
        [isOneTimeMagicTokenLink, t],
    )

    // The <a href> handles navigation / app hand-off natively; this only marks
    // the attempt (breadcrumb + one-time lock + pending feedback). The
    // app-absent store fallback is handled by LoginCoacheePage via the
    // `app_attempt` round-trip, not here.
    const handleOpenInApp = useCallback(() => {
        if (openInAppState !== 'idle') return
        if (isOneTimeMagicTokenLink && continueOnWebState !== 'idle') return

        if (isOneTimeMagicTokenLink) {
            setAllCardStates('pending')
        } else {
            setOpenInAppState('pending')
        }
        logBreadcrumb('open_in_app_clicked')
        setStatusMessage(t('MagicLinkChooser.openingApp'))

        scheduleReset(
            openInAppResetTimerRef,
            isOneTimeMagicTokenLink ? setAllCardStates : setOpenInAppState,
        )
    }, [
        continueOnWebState,
        isOneTimeMagicTokenLink,
        openInAppState,
        scheduleReset,
        setAllCardStates,
        t,
    ])

    const handleContinueOnWeb = useCallback(() => {
        if (continueOnWebState !== 'idle') return
        if (isOneTimeMagicTokenLink && openInAppState !== 'idle') return

        if (isOneTimeMagicTokenLink) {
            setAllCardStates('pending')
        } else {
            setContinueOnWebState('pending')
        }
        logBreadcrumb('continue_on_web_clicked')
        onContinueOnWeb()
        scheduleReset(
            continueOnWebResetTimerRef,
            isOneTimeMagicTokenLink ? setAllCardStates : setContinueOnWebState,
        )
    }, [
        continueOnWebState,
        isOneTimeMagicTokenLink,
        onContinueOnWeb,
        openInAppState,
        scheduleReset,
        setAllCardStates,
    ])

    const handleStoreBadgeClick = useCallback(() => {
        logBreadcrumb('download_app_clicked')
    }, [])

    const alreadyUsedBadge = t('MagicLinkChooser.alreadyUsedBadge')

    return (
        <AuthLayout
            mobile={mobile}
            bottomSlot={
                <AppDownloadCard
                    appStoreUrl={appStoreUrl}
                    playStoreUrl={playStoreUrl}
                    onBadgeClick={handleStoreBadgeClick}
                />
            }
            footer={
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        marginTop: 16,
                        color: COLORS.neutral500,
                        fontFamily: FONT.display,
                        fontSize: 12,
                        letterSpacing: '0.005em',
                    }}
                >
                    <LockIcon size={13} />
                    <span>{t('MagicLinkChooser.secureNote')}</span>
                </div>
            }
            statusNode={
                <div role="status" aria-live="polite" className="sr-only">
                    {statusMessage}
                </div>
            }
        >
            <h1
                style={{
                    fontSize: 28,
                    color: COLORS.noir,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.15,
                    margin: 0,
                    textAlign: 'center',
                }}
            >
                {pageTitle}
            </h1>

            <div
                style={{
                    marginTop: 28,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                }}
            >
                {mobile && (
                    <ActionCard
                        primary
                        ribbon={t('MagicLinkChooser.recommended')}
                        title={t('MagicLinkChooser.openInApp')}
                        href={openInAppUrl}
                        pending={openInAppState === 'pending'}
                        consumed={openInAppState === 'consumed'}
                        consumedLabel={alreadyUsedBadge}
                        onClick={handleOpenInApp}
                    />
                )}
                <ActionCard
                    primary={continueCardIsPrimary}
                    title={continueCardTitle}
                    pending={continueOnWebState === 'pending'}
                    consumed={continueOnWebState === 'consumed'}
                    consumedLabel={alreadyUsedBadge}
                    onClick={handleContinueOnWeb}
                />
            </div>
        </AuthLayout>
    )
}

export default MagicLinkChooser
